import React, { useState, useRef, useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { designSystem, Modal } from '@yola/ws-ui';
import { dialogs, i18next } from '@yola/ws-sdk';
import { Stepper, utils } from '@yola/webapp-plugins';
import { constants, verifiers, selectors } from '@yola/subscription-manager-js';
import yolaEditorDialogs from 'yola-editor/src/js/modules/dialogs';
import features from 'yola-editor/src/js/modules/features';
import { PUBLISHING } from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import B2cDomainPurchaseContainer from 'yola-editor/src/js/modules/upsell/containers/b2c-domain-purchase-container';
import PaywallContainer from 'yola-editor/src/js/modules/upsell/containers/paywall-container';
import actionIds from 'yola-editor/src/js/modules/upsell/constants/domain-purchase-action-ids.js';
import getSiteId from 'yola-editor/src/js/modules/auth/selectors/site-id';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import publishing from 'yola-editor/src/js/modules/publishing';
import segment from 'src/js/modules/analytics/segment';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';

import AutomaticPlanSelectorContainer from './automatic-plan-selector-container';

const {
  SubscriptionType: { B2C_HOSTING_FREE, DOMAIN_SUBSCRIPTION },
  SubscriptionTerm: { ANNUAL },
} = constants;
const {
  constants: { triggerIds },
} = segment;

const stepIds = {
  CHOOSE_DOMAIN: 'chooseDomain',
  SELECT_WEBSITE_PLAN: 'selectWebsitePlan',
  PUBLISH_WEBSITE: 'publishWebsite',
};
const stepsOrder = [stepIds.CHOOSE_DOMAIN, stepIds.SELECT_WEBSITE_PLAN, stepIds.PUBLISH_WEBSITE];

const { ButtonsHeader, Preloader, Box } = designSystem;

const FreeTrialPublishingFlowDialogContainer = () => {
  const modalContentRef = useRef(null);

  const subscriptionsList = useSelector(selectors.getSubscriptionList, shallowEqual);
  const activeHostingSubscription = useSelector(selectors.getActiveHostingSubscription);
  const activeSubscriptions = useSelector(selectors.getActiveSubscriptions);
  const siteId = useSelector(getSiteId);

  const dispatch = useDispatch();

  const subscriptionsTypes = subscriptionsList.map(({ type }) => type);
  const isFreeTrialAvailable = !verifiers.hasPaidHostingPackageType(subscriptionsTypes);

  const [data, setData] = useState(null);
  const [activeStepId, setActiveStepId] = useState(stepIds.CHOOSE_DOMAIN);
  const [isVisibleAutomaticPlanSelector, setIsVisibleAutomaticPlanSelector] =
    useState(isFreeTrialAvailable);

  const progress = ((stepsOrder.indexOf(activeStepId) + 1) / stepsOrder.length) * 100;

  const limits = features.accessors.getCurrentPackageFeatureLimits();
  const customDomainFeatureAvailable = limits.customDomain.available;
  const isSubscriptionExpired = verifiers.hasExpiredPaidSubscription(activeSubscriptions);
  const canPurchaseDomain = customDomainFeatureAvailable || isSubscriptionExpired;
  const isFreeDomainSelected = data?.actionId === actionIds.GET_FREE_DOMAIN;

  const redirectToPaymentPage = (domain, successUrl) => {
    dispatch(
      yolaEditorDialogs.actions.show(yolaEditorDialogs.dialogTypes.PENDING, {
        captions: {
          title: i18next.t('Loading...'),
        },
      })
    );

    const paymentParams = {
      products: [DOMAIN_SUBSCRIPTION],
      term: ANNUAL,
      latitude_site_id: siteId,
      domain_name: domain,
      ...(successUrl && { success_url: successUrl }),
    };

    const paymentUrl = utils.generatePaymentUrl(paymentParams);

    saveSiteWithEffect(() => {
      redirectToUrl(paymentUrl);
    });
  };

  const processPublishDomain = ({ domain, successUrl }) => {
    dialogs.operations.hide();

    if (isSubscriptionExpired) {
      dispatch(
        yolaEditorDialogs.actions.show(yolaEditorDialogs.dialogTypes.SUBSCRIPTION_EXPIRED, {
          onClose: () => dispatch(publishing.actions.publishWebsite(domain)),
          additionalPaymentParams: {
            successUrl,
          },
        })
      );
      return;
    }

    dispatch(publishing.actions.publishWebsite(domain));
  };

  const onDomainSelect = ({ domain, actionId, successUrl }) => {
    setData({ domain, actionId, successUrl });

    switch (actionId) {
      case actionIds.BUY_DOMAIN: {
        if (canPurchaseDomain) {
          redirectToPaymentPage(domain, successUrl);
          break;
        }

        setActiveStepId(stepIds.SELECT_WEBSITE_PLAN);
        break;
      }

      case actionIds.GET_FREE_DOMAIN: {
        if (activeHostingSubscription) {
          dialogs.operations.hide();
          dispatch(publishing.actions.publishWebsite());
          break;
        }

        setActiveStepId(stepIds.SELECT_WEBSITE_PLAN);
        break;
      }

      case actionIds.EXISTING_DOMAIN: {
        if (canPurchaseDomain) {
          processPublishDomain({ domain, successUrl });
          break;
        }

        setActiveStepId(stepIds.SELECT_WEBSITE_PLAN);
        break;
      }

      case actionIds.THIRD_PARTY_DOMAIN: {
        if (customDomainFeatureAvailable) {
          dispatch(
            yolaEditorDialogs.actions.show(yolaEditorDialogs.dialogTypes.PENDING, {
              captions: {
                title: i18next.t('Loading...'),
              },
            })
          );

          redirectToUrl(successUrl);
          break;
        }

        if (isSubscriptionExpired) {
          dialogs.operations.hide();
          dispatch(
            yolaEditorDialogs.actions.show(yolaEditorDialogs.dialogTypes.SUBSCRIPTION_EXPIRED, {
              additionalPaymentParams: {
                successUrl,
              },
            })
          );
          break;
        }

        setActiveStepId(stepIds.SELECT_WEBSITE_PLAN);
        break;
      }
      default: {
        break;
      }
    }
  };

  const onAutomaticPlanSelectorCancel = () => {
    setIsVisibleAutomaticPlanSelector(false);
  };

  const getPrevStepId = () => {
    const currentStepIndex = stepsOrder.indexOf(activeStepId);
    return stepsOrder[currentStepIndex - 1];
  };

  const onBackClick = () => {
    const prevStepId = getPrevStepId();

    if (!prevStepId) {
      dialogs.operations.hide();
    }

    if (isFreeTrialAvailable) {
      setIsVisibleAutomaticPlanSelector(true);
    }

    setActiveStepId(prevStepId);
  };

  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo(0, 0);
    }
  }, [activeStepId, isVisibleAutomaticPlanSelector]);

  return (
    <Modal
      fullscreen
      draggable={false}
      resizable={false}
      className="ws-free-trial-publishing-flow-dialog"
      modalContentRef={modalContentRef}
    >
      <div>
        <ButtonsHeader
          isSticky={false}
          prevButton={{ label: i18next.t('Back'), iconGlyph: 'back', onClick: onBackClick }}
        />
        <Preloader isRounded isAnimated progress={progress} />
      </div>
      <Box paddingTop="spacing-m" breakpoints={{ sm: { paddingTop: 'spacing-2-xl' } }}>
        <Stepper activeStepId={activeStepId}>
          <Stepper.TitlesList>
            <Stepper.Title id={stepIds.CHOOSE_DOMAIN} title={i18next.t('#1 Choose a domain')} />
            <Stepper.Title
              id={stepIds.SELECT_WEBSITE_PLAN}
              title={
                isFreeTrialAvailable
                  ? i18next.t('#2 Start free trial')
                  : i18next.t('#2 Select website plan')
              }
              {...(!isFreeDomainSelected && {
                label: i18next.t('Required'),
              })}
            />
            <Stepper.Title id={stepIds.PUBLISH_WEBSITE} title={i18next.t('#3 Publish website')} />
          </Stepper.TitlesList>
          <Stepper.ContentList>
            <Stepper.Content id={stepIds.CHOOSE_DOMAIN}>
              <B2cDomainPurchaseContainer
                shouldPublishOnSuccess
                withFreeSubdomain
                withPurchasedDomains
                triggerId={triggerIds.PUBLISH_INTERRUPTION}
                premiumFeature={PUBLISHING}
                view="content"
                captionsOverrides={{
                  title: i18next.t('Choose Your Domain'),
                }}
                onSelect={onDomainSelect}
              />
            </Stepper.Content>
            <Stepper.Content id={stepIds.SELECT_WEBSITE_PLAN}>
              {data &&
                (isVisibleAutomaticPlanSelector ? (
                  <AutomaticPlanSelectorContainer
                    actionId={data.actionId}
                    domainName={data.domain}
                    successUrl={data.successUrl}
                    onCancel={onAutomaticPlanSelectorCancel}
                  />
                ) : (
                  <PaywallContainer
                    view="content"
                    actionId={data.actionId}
                    domainName={data.domain}
                    successUrl={data.successUrl}
                    upsellType={PUBLISHING}
                    isFreePlanAvailable={isFreeDomainSelected}
                    {...(!isFreeDomainSelected && { disabledPlans: [B2C_HOSTING_FREE] })}
                  />
                ))}
            </Stepper.Content>
          </Stepper.ContentList>
        </Stepper>
      </Box>
    </Modal>
  );
};

export default FreeTrialPublishingFlowDialogContainer;
